// v2.9.0 — FeaturedCards : grille de cards rendues sous les onglets quand
// la conversation est vide (zone "discovery"). Adopte le langage visuel de
// <ImmersiveCard> sans réutiliser son layout pixel-pour-pixel (cartes
// rectangulaires plus larges pour title + subtitle + actions).
//
// Props :
//   - items : Array<{ id, title, subtitle, pill, image_override?, action_url?, action_label?, ...specific }>
//   - categoryImage : { src, accent, tagline } — image de la catégorie pour fallback
//   - onItemClick : (item) => void — callback global (avant que les actions individuelles tirent)
//   - emptyHint : string — message affiché quand items est vide
//   - testIdPrefix : "featured" | "live"

import { Plus as IconPlus } from "lucide-react";

export default function FeaturedCards({
  items = [],
  categoryImage = {},
  onItemClick,
  emptyHint = "Aucun contenu pour cette catégorie.",
  testIdPrefix = "featured",
  showPrice = false,
  extraActions = null,  // v2.11.0 — Fn(item) => ReactNode placé sous les autres actions.
  onLearnMore = null,   // v2.15.4 — Fn(item) → déclenche query LLM "En savoir plus".
}) {
  if (!items.length) {
    return (
      <div
        data-testid={`${testIdPrefix}-empty`}
        style={{
          padding: "32px 16px",
          textAlign: "center",
          color: "rgba(255,255,255,0.55)",
          fontSize: 13,
        }}
      >
        {emptyHint}
      </div>
    );
  }
  const accent = categoryImage.accent || "#FF7E1B";

  return (
    <div
      className="civ-featured-row"
      data-testid={`${testIdPrefix}-grid`}
      style={{
        // v2.15.4 — Horizontal scroll snap (au lieu de grid).
        // v2.19.39-sec9 — Full-bleed : padding latéral à 0, les cards touchent les bords.
        display: "flex",
        overflowX: "auto",
        gap: 12,
        padding: "8px 0 18px",
        scrollSnapType: "x mandatory",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {items.map((item) => {
        const img = item.image_override || categoryImage.src || null;
        const priceLabel = showPrice && item.ticket_price_fcfa != null
          ? (item.ticket_price_fcfa === 0 ? "Gratuit" : `${item.ticket_price_fcfa.toLocaleString("fr-FR")} FCFA`)
          : null;
        const statusBadge = item.status === "live_now" ? { txt: "EN COURS", bg: "#EF4444" }
                          : item.status === "today" ? { txt: "AUJOURD'HUI", bg: "#F59E0B" }
                          : item.status === "tomorrow" ? { txt: "DEMAIN", bg: "#0EA5E9" }
                          : null;
        return (
          <article
            key={item.id}
            data-testid={`${testIdPrefix}-card-${item.id}`}
            style={{
              position: "relative",
              borderRadius: 14,
              overflow: "hidden",
              // v2.19.39-sec13 — Fond très proche du chat root (#0f1115) pour
              // éliminer l'effet "rectangle dans rectangle" perçu sur fond sombre.
              background: "#181b21",
              border: "none",
              // box-shadow minimal pour préserver la profondeur SANS créer de contour
              // visible (élévation subtile au survol seulement).
              boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              cursor: onItemClick ? "pointer" : "default",
              transition: "transform 200ms ease, box-shadow 200ms ease",
              flex: "0 0 240px",
              width: 240,
              scrollSnapAlign: "start",
            }}
            onClick={() => onItemClick && onItemClick(item)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 4px 12px rgba(0,0,0,0.4), 0 0 0 1px ${accent}55`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.18)";
            }}
          >
            <div style={{ position: "relative", height: 110, background: "#1e293b" }}>
              {img ? (
                <img
                  src={img}
                  alt=""
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : (
                <div style={{
                  width: "100%", height: "100%",
                  background: `linear-gradient(135deg, ${accent}88, ${accent}33)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontWeight: 800, fontSize: 24,
                }}>
                  {(item.title || "?").slice(0, 1).toUpperCase()}
                </div>
              )}
              <div style={{
                position: "absolute", inset: 0,
                background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.75) 100%)",
              }} />
              {statusBadge && (
                <span style={{
                  position: "absolute", top: 8, left: 8,
                  background: statusBadge.bg, color: "#fff",
                  padding: "2px 8px", borderRadius: 999,
                  fontSize: 9.5, fontWeight: 800, letterSpacing: "0.05em",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                }}>{statusBadge.txt}</span>
              )}
              {item.pill && (
                <span style={{
                  position: "absolute", top: 8, right: 8,
                  background: "rgba(15,23,42,0.85)", color: "#fff",
                  padding: "2px 8px", borderRadius: 999,
                  fontSize: 9.5, fontWeight: 700,
                  border: `1px solid ${accent}`,
                }}>{item.pill}</span>
              )}
            </div>

            <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: "#fff", lineHeight: 1.25 }}>
                {item.title}
              </div>
              {item.subtitle && (
                <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.65)", lineHeight: 1.35 }}>
                  {item.subtitle}
                </div>
              )}
              {item.venue_name && (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>📍 {item.venue_name} · {item.commune}</div>
              )}
              {priceLabel && (
                <div style={{ fontSize: 11.5, color: accent, fontWeight: 700, marginTop: 2 }}>
                  🎟 {priceLabel}
                </div>
              )}
              {(item.action_url || item.action_label) && (
                <div style={{ marginTop: 6 }}>
                  {item.action_url ? (
                    <a
                      href={item.action_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`${testIdPrefix}-action-${item.id}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: "inline-block",
                        background: accent, color: "#fff",
                        padding: "5px 12px", borderRadius: 8,
                        fontSize: 11.5, fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      {item.action_label || "Voir"} →
                    </a>
                  ) : (
                    <button
                      type="button"
                      data-testid={`${testIdPrefix}-action-${item.id}`}
                      onClick={(e) => { e.stopPropagation(); onItemClick && onItemClick(item); }}
                      style={{
                        background: accent, color: "#fff", border: "none",
                        padding: "5px 12px", borderRadius: 8,
                        fontSize: 11.5, fontWeight: 600, cursor: "pointer",
                      }}
                    >
                      {item.action_label || "Voir"}
                    </button>
                  )}
                </div>
              )}
              {/* v2.11.0 — Slot d'actions extras (ex: bouton "🎟 Réserver" sur cards Live). */}
              {extraActions && (
                <div style={{ marginTop: 6 }} onClick={(e) => e.stopPropagation()}>
                  {extraActions(item)}
                </div>
              )}
              {/* v2.15.4 — Bouton "En savoir plus" → query LLM.
                  v2.19.39-sec11 — Icône lucide Plus monochrome (plus d'emoji "+"). */}
              {onLearnMore && (
                <div style={{ marginTop: 6 }} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    data-testid="card-learn-more"
                    onClick={() => onLearnMore(item)}
                    style={{
                      width: "100%",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      color: "#94a3b8",
                      padding: "5px 10px",
                      borderRadius: 8,
                      fontSize: 11.5,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  ><IconPlus width={14} height={14} strokeWidth={1.75} /> En savoir plus</button>
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
